import { Rocket, X } from "lucide-react";
import { useTranslation } from "@/packages/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import type { AuditId } from "@/packages/types";
import { Button } from "@/components/ui";
import { WidgetCard, WidgetCardSkeleton } from "./WidgetCard";

interface GetStartedCardProps {
  checklistTotal: number;
  documentTotal: number;
  auditId: AuditId;
  onGoToChecklist: () => void;
  index?: number;
}

function getStorageKey(auditId: AuditId) {
  return `audit-get-started-dismissed-${auditId}`;
}

export function GetStartedCard({
  checklistTotal,
  documentTotal,
  auditId,
  onGoToChecklist,
  index,
}: GetStartedCardProps) {
  const { t } = useTranslation("audits");
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(getStorageKey(auditId));
    if (stored === "true") {
      setDismissed(true);
    }
  }, [auditId]);

  // Hide when both have data
  if (checklistTotal > 0 && documentTotal > 0) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(getStorageKey(auditId), "true");
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.div
          key="get-started"
          exit={{ opacity: 0, scale: 0.95, height: 0 }}
          transition={{ duration: 0.2 }}
          className="h-full"
        >
          <WidgetCard
            size="auto"
            title={t("overview.getStarted.title")}
            icon={<Rocket size={16} />}
            className="bg-white border-accent/15 border-dashed h-full"
            action={
              <button
                type="button"
                className="text-muted hover:text-secondary transition-colors"
                onClick={handleDismiss}
                aria-label={t("overview.getStarted.dismiss")}
              >
                <X size={14} />
              </button>
            }
            index={index}
          >
            <div className="flex flex-col items-center justify-center h-full gap-3">
              <p className="text-sm text-secondary text-center">
                {t("overview.getStarted.checklistPrompt")}
              </p>
              <Button variant="secondary" size="sm" onClick={onGoToChecklist}>
                {t("overview.getStarted.goToChecklist")}
              </Button>
            </div>
          </WidgetCard>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function GetStartedCardSkeleton({ index }: { index?: number }) {
  return <WidgetCardSkeleton size="auto" index={index} />;
}
